import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Archive, Check, RotateCcw, Send, Star } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { DataListCardField } from "@/components/shared/DataListCardField";
import { DataListCardRow } from "@/components/shared/DataListCardRow";
import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import { ResponsiveListSplit } from "@/components/shared/ResponsiveListSplit";
import {
  StatusBadge,
  type StatusBadgeVariant,
} from "@/components/shared/StatusBadge";
import { TableCard } from "@/components/shared/TableCard";
import { TablePagination } from "@/components/shared/TablePagination";
import { SendSurveyDialog } from "@/pages/feedback/send-survey-dialog";
import {
  TableActionsMenu,
  type TableAction,
} from "@/components/shared/TableActionsMenu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { usePaginationFromUrl } from "@/hooks/usePaginationFromUrl";
import { formatDisplayDate } from "@/lib/dateFormat";
import { cn } from "@/lib/utils";
import {
  deriveDisplayStatus,
  getAdminFeedbackStats,
  listAdminRequestFeedback,
  updateAdminRequestFeedbackStatus,
  type AdminRequestFeedbackItem,
  type AdminStatusFilter,
  type DisplayStatus,
  type RequestFeedbackRequestType,
  type RequestFeedbackStatus,
} from "@/services/requestFeedbackService";
import {
  REQUEST_FEEDBACK_REQUEST_TYPES,
  requestTypeLabel,
} from "./request-type-label";

const QUERY_KEY = ["platform", "request-feedback"] as const;

function npsBucketClass(score: number): string {
  if (score >= 9) return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300";
  if (score >= 7) return "bg-amber-500/15 text-amber-700 dark:text-amber-300";
  return "bg-red-500/15 text-red-700 dark:text-red-300";
}

function StarsReadOnly({
  value,
  ariaLabel,
}: {
  value: number | null;
  ariaLabel: string;
}): JSX.Element {
  if (value == null) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={ariaLabel}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={cn(
            "h-4 w-4",
            n <= value
              ? "fill-yellow-400 text-yellow-400"
              : "fill-transparent text-muted-foreground"
          )}
        />
      ))}
    </span>
  );
}

export function RequestRatingsPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pagination = usePaginationFromUrl();
  const queryClient = useQueryClient();
  const [sendDialogOpen, setSendDialogOpen] = useState(false);

  const statusParam = searchParams.get("status") ?? "all";
  const requestTypeParam = searchParams.get("requestType") ?? "all";

  const statusFilter: AdminStatusFilter | undefined =
    statusParam === "queued" ||
    statusParam === "sent" ||
    statusParam === "submitted" ||
    statusParam === "reviewed" ||
    statusParam === "archived"
      ? statusParam
      : undefined;

  const requestTypeFilter: RequestFeedbackRequestType | undefined =
    requestTypeParam === "notarial" || requestTypeParam === "accountant"
      ? requestTypeParam
      : undefined;

  const setSearchParam = useCallback(
    (key: string, value: string, defaultValue: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === defaultValue) next.delete(key);
          else next.set(key, value);
          next.set("page", "1");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const listParams = useMemo(
    () => ({
      page: pagination.page,
      limit: pagination.limit,
      status: statusFilter,
      requestType: requestTypeFilter,
    }),
    [pagination.page, pagination.limit, statusFilter, requestTypeFilter]
  );

  const statsParams = useMemo(
    () => ({ status: statusFilter, requestType: requestTypeFilter }),
    [statusFilter, requestTypeFilter]
  );

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: [...QUERY_KEY, "list", listParams],
    queryFn: () => listAdminRequestFeedback(listParams),
  });

  const statsQuery = useQuery({
    queryKey: [...QUERY_KEY, "stats", statsParams],
    queryFn: () => getAdminFeedbackStats(statsParams),
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({
      id,
      status,
    }: {
      id: string;
      status: RequestFeedbackStatus;
    }) => updateAdminRequestFeedbackStatus(id, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: () => {
      toast.error(t("ratings.page.statusUpdateError"));
    },
  });

  const getActions = useCallback(
    (row: AdminRequestFeedbackItem): TableAction[] => {
      const actions: TableAction[] = [];
      if (row.status === "submitted") {
        actions.push({
          label: t("ratings.page.markReviewed"),
          icon: Check,
          onClick: () =>
            updateStatusMutation.mutate({ id: row.id, status: "reviewed" }),
        });
      }
      if (row.status === "submitted" || row.status === "reviewed") {
        actions.push({
          label: t("ratings.page.markArchived"),
          icon: Archive,
          onClick: () =>
            updateStatusMutation.mutate({ id: row.id, status: "archived" }),
        });
      }
      if (row.status === "archived" || row.status === "reviewed") {
        actions.push({
          label: t("ratings.page.markSubmitted"),
          icon: RotateCcw,
          onClick: () =>
            updateStatusMutation.mutate({ id: row.id, status: "submitted" }),
        });
      }
      return actions;
    },
    [t, updateStatusMutation]
  );

  const displayStatusBadge = (display: DisplayStatus): JSX.Element => {
    // 'exhausted' must be listed explicitly: both chains end in a catch-all
    // that renders "Archivada" in red, so any status added to the backend and
    // not added here silently reads as archived — which is what happened when
    // the reminder cadence shipped.
    const variant: StatusBadgeVariant =
      display === "submitted"
        ? "success"
        : display === "sent"
          ? "warning"
          : display === "queued"
            ? "info"
            : display === "reviewed"
              ? "info"
              : display === "exhausted"
                ? "neutral"
                : "softDanger";
    const label =
      display === "queued"
        ? t("ratings.page.statusQueued")
        : display === "sent"
          ? t("ratings.page.statusSent")
          : display === "submitted"
            ? t("ratings.page.statusSubmitted")
            : display === "reviewed"
              ? t("ratings.page.statusReviewed")
              : display === "exhausted"
                ? t("ratings.page.statusExhausted")
                : t("ratings.page.statusArchived");
    return <StatusBadge variant={variant}>{label}</StatusBadge>;
  };

  useEffect(() => {
    if (data != null) {
      pagination.setTotal(data.total);
    } else if (isError) {
      pagination.setTotal(0);
    }
  }, [data, isError, pagination]);

  useEffect(() => {
    if (isError) {
      toast.error(t("ratings.page.error"));
    }
  }, [isError, t]);

  const items = data?.data ?? [];
  const loading = isLoading;
  const stats = statsQuery.data;

  const typeLabel = (type: RequestFeedbackRequestType): string =>
    requestTypeLabel(t, type);

  const handleSent = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  }, [queryClient]);

  const goToDetail = (id: string): void => navigate(`/ratings/${id}`);

  return (
    <div className="space-y-4 p-4 md:p-8">
      <PageHeader
        title={t("ratings.page.title")}
        description={t("ratings.page.description")}
        actions={
          <Button onClick={() => setSendDialogOpen(true)}>
            <Send className="mr-2 h-4 w-4" />
            {t("ratings.send.openButton")}
          </Button>
        }
      />
      <SendSurveyDialog
        open={sendDialogOpen}
        onOpenChange={setSendDialogOpen}
        onSent={handleSent}
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t("ratings.stats.nps")}
            </p>
            <p className="mt-1 text-3xl font-bold leading-none">
              {stats?.npsScore != null ? stats.npsScore : "—"}
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              {t("ratings.stats.responses", { count: stats?.total ?? 0 })}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t("ratings.stats.breakdown")}
            </p>
            <div className="mt-2 space-y-1 text-sm">
              <p className="flex justify-between">
                <span className="text-emerald-700 dark:text-emerald-400">
                  {t("ratings.nps.promoter")}
                </span>
                <span className="font-medium">{stats?.promoters ?? 0}</span>
              </p>
              <p className="flex justify-between">
                <span className="text-amber-700 dark:text-amber-400">
                  {t("ratings.nps.passive")}
                </span>
                <span className="font-medium">{stats?.passives ?? 0}</span>
              </p>
              <p className="flex justify-between">
                <span className="text-red-700 dark:text-red-400">
                  {t("ratings.nps.detractor")}
                </span>
                <span className="font-medium">{stats?.detractors ?? 0}</span>
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t("ratings.stats.platformAverage")}
            </p>
            <p className="mt-1 text-3xl font-bold leading-none">
              {stats?.platformAverage != null
                ? stats.platformAverage.toFixed(1)
                : "—"}
              <span className="ml-1 text-base text-muted-foreground">/5</span>
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t("ratings.stats.professionalAverage")}
            </p>
            <p className="mt-1 text-3xl font-bold leading-none">
              {stats?.professionalAverage != null
                ? stats.professionalAverage.toFixed(1)
                : "—"}
              <span className="ml-1 text-base text-muted-foreground">/5</span>
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={statusParam}
          onValueChange={(v) => setSearchParam("status", v, "all")}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={t("ratings.page.filterStatus")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("ratings.page.filterAll")}</SelectItem>
            <SelectItem value="queued">
              {t("ratings.page.statusQueued")}
            </SelectItem>
            <SelectItem value="sent">
              {t("ratings.page.statusSent")}
            </SelectItem>
            <SelectItem value="submitted">
              {t("ratings.page.statusSubmitted")}
            </SelectItem>
            <SelectItem value="reviewed">
              {t("ratings.page.statusReviewed")}
            </SelectItem>
            <SelectItem value="archived">
              {t("ratings.page.statusArchived")}
            </SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={requestTypeParam}
          onValueChange={(v) => setSearchParam("requestType", v, "all")}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder={t("ratings.page.filterType")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("ratings.page.filterAll")}</SelectItem>
            {/*
              Derived from the same record the labels come from: a hardcoded
              pair here would leave a new request type filterable by URL but
              absent from the control that is supposed to list them.
            */}
            {REQUEST_FEEDBACK_REQUEST_TYPES.map((type) => (
              <SelectItem key={type} value={type}>
                {typeLabel(type)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <TableCard>
        {loading ? (
          <div className="flex min-h-[40vh] items-center justify-center py-12">
            <LoadingLogo size="lg" />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title={t("ratings.page.emptyTitle")}
            message={t("ratings.page.emptyMessage")}
          />
        ) : (
          <>
            <ResponsiveListSplit
              table={
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("ratings.page.user")}</TableHead>
                      <TableHead>{t("ratings.stats.nps")}</TableHead>
                      <TableHead>{t("ratings.page.platformRating")}</TableHead>
                      <TableHead>{t("ratings.page.submittedAt")}</TableHead>
                      <TableHead>{t("ratings.page.status")}</TableHead>
                      <TableHead className="w-[60px]" aria-hidden />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((row) => {
                      const actions = getActions(row);
                      return (
                        <TableRow
                          key={row.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => goToDetail(row.id)}
                        >
                          <TableCell>
                            <div className="font-medium text-foreground">
                              {row.consumerName?.trim() ||
                                row.consumerEmail ||
                                "—"}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {typeLabel(row.requestType)}
                              {row.productLabel ? ` · ${row.productLabel}` : ""}
                            </div>
                          </TableCell>
                          <TableCell>
                            {row.npsScore != null ? (
                              <span
                                className={cn(
                                  "inline-flex h-7 w-9 items-center justify-center rounded-md text-sm font-semibold",
                                  npsBucketClass(row.npsScore)
                                )}
                              >
                                {row.npsScore}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <StarsReadOnly
                              value={row.platformRating}
                              ariaLabel={t("ratings.page.platformRating")}
                            />
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-muted-foreground">
                            {row.submittedAt
                              ? formatDisplayDate(row.submittedAt, {
                                  includeTime: true,
                                  locale: i18n.language,
                                })
                              : "—"}
                          </TableCell>
                          <TableCell>
                            {displayStatusBadge(deriveDisplayStatus(row))}
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            {actions.length > 0 ? (
                              <TableActionsMenu actions={actions} />
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              }
              cards={
                <div className="divide-y divide-border">
                  {items.map((row) => {
                    const actions = getActions(row);
                    return (
                      <DataListCardRow
                        key={row.id}
                        onClick={() => goToDetail(row.id)}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold leading-snug text-foreground">
                              {row.consumerName?.trim() ||
                                row.consumerEmail ||
                                "—"}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {typeLabel(row.requestType)}
                              {row.productLabel ? ` · ${row.productLabel}` : ""}
                            </p>
                          </div>
                          {actions.length > 0 ? (
                            <div
                              className="shrink-0"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <TableActionsMenu actions={actions} />
                            </div>
                          ) : null}
                        </div>
                        <div className="mt-3 grid grid-cols-2 gap-3">
                          <DataListCardField label={t("ratings.stats.nps")}>
                            {row.npsScore != null ? (
                              <span
                                className={cn(
                                  "inline-flex h-7 w-9 items-center justify-center rounded-md text-sm font-semibold",
                                  npsBucketClass(row.npsScore)
                                )}
                              >
                                {row.npsScore}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </DataListCardField>
                          <DataListCardField
                            label={t("ratings.page.platformRating")}
                          >
                            <StarsReadOnly
                              value={row.platformRating}
                              ariaLabel={t("ratings.page.platformRating")}
                            />
                          </DataListCardField>
                          <DataListCardField
                            label={t("ratings.page.submittedAt")}
                          >
                            <span className="text-muted-foreground">
                              {row.submittedAt
                                ? formatDisplayDate(row.submittedAt, {
                                    includeTime: true,
                                    locale: i18n.language,
                                  })
                                : "—"}
                            </span>
                          </DataListCardField>
                          <DataListCardField label={t("ratings.page.status")}>
                            {displayStatusBadge(deriveDisplayStatus(row))}
                          </DataListCardField>
                        </div>
                      </DataListCardRow>
                    );
                  })}
                </div>
              }
            />
            <TablePagination
              pagination={pagination}
              rowsPerPageLabel={t("ratings.page.rowsPerPage")}
              pageOfLabel={t("ratings.page.pageOf", {
                from: (pagination.page - 1) * pagination.limit + 1,
                to: Math.min(
                  pagination.page * pagination.limit,
                  pagination.total
                ),
                total: pagination.total,
              })}
              onRefresh={() => {
                void refetch();
                void statsQuery.refetch();
              }}
              isRefreshing={isFetching || statsQuery.isFetching}
            />
          </>
        )}
      </TableCard>
    </div>
  );
}
